import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface DiffEntry {
  field: string;
  oldValue: unknown;
  newValue: unknown;
  status: 'added' | 'removed' | 'changed' | 'unchanged';
}

export function computeDiff(
  oldValueStr: string | null,
  newValueStr: string | null
): DiffEntry[] {
  // Both null means no diff
  if (oldValueStr === null && newValueStr === null) {
    return [];
  }

  let oldJson: Record<string, unknown> | null = null;
  let newJson: Record<string, unknown> | null = null;

  // Parse JSON with error handling
  if (oldValueStr) {
    try {
      oldJson = JSON.parse(oldValueStr);
    } catch {
      return [];
    }
  }

  if (newValueStr) {
    try {
      newJson = JSON.parse(newValueStr);
    } catch {
      return [];
    }
  }

  const entries: DiffEntry[] = [];
  const allKeys = new Set<string>();

  if (oldJson) {
    Object.keys(oldJson).forEach((key) => allKeys.add(key));
  }

  if (newJson) {
    Object.keys(newJson).forEach((key) => allKeys.add(key));
  }

  const sortedKeys = Array.from(allKeys).sort();

  sortedKeys.forEach((field) => {
    const oldVal = oldJson?.[field] ?? null;
    const newVal = newJson?.[field] ?? null;

    let status: DiffEntry['status'];
    if (oldVal === null && newVal !== null) {
      status = 'added';
    } else if (oldVal !== null && newVal === null) {
      status = 'removed';
    } else if (oldVal !== newVal) {
      status = 'changed';
    } else {
      status = 'unchanged';
    }

    entries.push({
      field,
      oldValue: oldVal,
      newValue: newVal,
      status,
    });
  });

  return entries;
}

function formatValue(value: unknown): string {
  if (value === null) {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'string') {
    // Try to detect if it's a date
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      try {
        const date = new Date(value);
        return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }).format(date);
      } catch {
        return value;
      }
    }
    return value;
  }

  if (typeof value === 'number') {
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  }

  return String(value);
}

interface AuditDiffProps {
  oldValue: string | null;
  newValue: string | null;
}

export function AuditDiff({ oldValue, newValue }: AuditDiffProps) {
  const [showAll, setShowAll] = useState(false);
  const diff = computeDiff(oldValue, newValue);

  if (diff.length === 0) {
    return null;
  }

  const changedEntries = diff.filter((e) => e.status !== 'unchanged');
  const unchangedEntries = diff.filter((e) => e.status === 'unchanged');
  const hasUnchanged = unchangedEntries.length > 0;

  const entriesToShow = showAll ? diff : changedEntries;

  const getRowClass = (status: DiffEntry['status']): string => {
    switch (status) {
      case 'added':
        return 'bg-green-50';
      case 'removed':
        return 'bg-red-50';
      case 'changed':
        return 'bg-amber-50';
      case 'unchanged':
        return 'bg-gray-50';
      default:
        return '';
    }
  };

  return (
    <div className="mt-3 space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="px-3 py-2 text-left font-semibold">Field</th>
              <th className="px-3 py-2 text-left font-semibold">Old Value</th>
              <th className="px-3 py-2 text-left font-semibold">New Value</th>
            </tr>
          </thead>
          <tbody>
            {entriesToShow.map((entry) => (
              <tr key={entry.field} className={`border-b ${getRowClass(entry.status)}`}>
                <td className="px-3 py-2 font-medium">{entry.field}</td>
                <td className="px-3 py-2">{formatValue(entry.oldValue)}</td>
                <td className="px-3 py-2">{formatValue(entry.newValue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasUnchanged && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowAll(!showAll)}
          className="h-7 gap-1 px-2 text-xs"
        >
          {showAll ? (
            <>
              <ChevronDown className="h-4 w-4" />
              Hide unchanged fields
            </>
          ) : (
            <>
              <ChevronRight className="h-4 w-4" />
              Show all fields
            </>
          )}
        </Button>
      )}
    </div>
  );
}
